import {
  $applyNodeReplacement,
  $isTextNode,
  TextNode,
  type DOMConversion,
  type DOMConversionMap,
  type DOMConversionOutput,
  type LexicalNode,
  type NodeKey,
  type SerializedTextNode,
} from "lexical"

/** Preserves color/font-size on HTML paste; registered as TextNode replacement. */
export class ExtendedTextNode extends TextNode {
  constructor(text: string, key?: NodeKey) {
    super(text, key)
  }

  static getType(): string {
    return "extended-text"
  }

  static clone(node: ExtendedTextNode): ExtendedTextNode {
    return new ExtendedTextNode(node.__text, node.__key)
  }

  static importDOM(): DOMConversionMap | null {
    const importers = TextNode.importDOM()
    return {
      ...importers,
      code: () => ({
        conversion: patchStyleConversion(importers?.code),
        priority: 1,
      }),
      em: () => ({
        conversion: patchStyleConversion(importers?.em),
        priority: 1,
      }),
      i: () => ({
        conversion: patchStyleConversion(importers?.i),
        priority: 1,
      }),
      b: () => ({
        conversion: patchStyleConversion(importers?.b),
        priority: 1,
      }),
      font: () => ({
        conversion: patchStyleConversion(importers?.span),
        priority: 1,
      }),
      span: () => ({
        conversion: patchStyleConversion(importers?.span),
        priority: 1,
      }),
      strong: () => ({
        conversion: patchStyleConversion(importers?.strong),
        priority: 1,
      }),
      sub: () => ({
        conversion: patchStyleConversion(importers?.sub),
        priority: 1,
      }),
      sup: () => ({
        conversion: patchStyleConversion(importers?.sup),
        priority: 1,
      }),
      u: () => ({
        conversion: patchStyleConversion(importers?.u),
        priority: 1,
      }),
      s: () => ({
        conversion: patchStyleConversion(importers?.s),
        priority: 1,
      }),
    }
  }

  static importJSON(serializedNode: SerializedTextNode): TextNode {
    return $createExtendedTextNode().updateFromJSON(serializedNode)
  }

  isSimpleText(): boolean {
    return this.__type === "extended-text" && this.__mode === 0
  }
}

export function $createExtendedTextNode(text = ""): ExtendedTextNode {
  return $applyNodeReplacement(new ExtendedTextNode(text))
}

export function $isExtendedTextNode(node: LexicalNode | null | undefined): node is ExtendedTextNode {
  return node instanceof ExtendedTextNode
}

function patchStyleConversion(
  originalDOMConverter?: (node: HTMLElement) => DOMConversion | null
): (node: HTMLElement) => DOMConversionOutput | null {
  return (node) => {
    const original = originalDOMConverter?.(node)
    const originalOutput = original?.conversion(node) ?? { node: null }
    if (!originalOutput) return originalOutput

    const backgroundColor = node.style.backgroundColor
    const color = node.style.color || node.getAttribute("color") || ""
    const fontFamily = node.style.fontFamily || node.getAttribute("face") || ""
    const fontSize = node.style.fontSize || legacyFontSize(node.getAttribute("size"))
    const { letterSpacing, lineHeight } = node.style

    return {
      ...originalOutput,
      forChild: (lexicalNode, parent) => {
        const originalForChild = originalOutput.forChild ?? ((x: LexicalNode) => x)
        const result = originalForChild(lexicalNode, parent)
        if (!$isTextNode(result)) return result

        const style = [
          backgroundColor ? `background-color: ${backgroundColor}` : null,
          color ? `color: ${color}` : null,
          fontFamily ? `font-family: ${fontFamily}` : null,
          fontSize ? `font-size: ${fontSize}` : null,
          letterSpacing ? `letter-spacing: ${letterSpacing}` : null,
          lineHeight ? `line-height: ${lineHeight}` : null,
        ]
          .filter((value): value is string => value != null)
          .join("; ")

        if (style.length) {
          const existing = result.getStyle()
          return result.setStyle(existing ? `${existing}; ${style}` : style)
        }
        return result
      },
    }
  }
}

function legacyFontSize(size: string | null): string {
  if (!size) return ""

  return (
    {
      "1": "10px",
      "2": "13px",
      "3": "16px",
      "4": "18px",
      "5": "24px",
      "6": "32px",
      "7": "48px",
    }[size] ?? ""
  )
}
